import { useState, useEffect } from "react";
import { Tabs, Card } from "antd";
import {
  TeamOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import EmployeeList from "./EmployeeList";
import EmployeeEditModal from "./EmployeeEditModal";
import AddEmployeeModal from "./AddEmployeeModal";
import PermissionOverview from "./PermissionOverview";
import LicensesTab, { type RoleQuota } from "./LicensesTab";
import InvoiceTab from "./InvoiceTab";
import { type Employee, type EmployeeTag } from "./types";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { apiUserGetMe } from "@/api";
import { useFeature } from "@/config/useFeature";

function EmployeesPage() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allTags, setAllTags] = useState<EmployeeTag[]>([]);
  const [roleQuotas, setRoleQuotas] = useState<RoleQuota[]>([]);
  const [isAdminOrOwner, setIsAdminOrOwner] = useState(false);
  const invoiceEnabled = useFeature("employeeInvoice");

  const fetchTags = () => {
    orpcClient.employeeTag.list({}).then((data) => {
      setAllTags((data ?? []).map((t: any) => ({ id: t.id, name: t.name })));
    });
  };

  const fetchEmployees = () => {
    orpcClient.employee.list({}).then((data) => {
      setEmployees(
        data.map((emp) => ({
          id: emp.id,
          name: emp.name,
          email: emp.user.email,
          avatar: emp.profile ?? undefined,
          birthday: emp.birthday
            ? new Date(emp.birthday).toISOString().split("T")[0]
            : undefined,
          bloodType: emp.bloodType ?? undefined,
          idNumber: emp.nationalIdNo ?? undefined,
          hasMotorcycleLicense: emp.motorbikeLicense ?? undefined,
          hasCarLicense: emp.carLicense ?? undefined,
          city: emp.address?.city ?? undefined,
          district: emp.address?.district ?? undefined,
          postalCode: emp.address?.postalCode?.toString() ?? undefined,
          address: emp.address?.address ?? undefined,
          emergencyContact: emp.emergencyContact ?? undefined,
          emergencyRelation: emp.emergencyContactRelationship ?? undefined,
          emergencyPhone: emp.emergencyContactPhoneNumber ?? undefined,
          notes: emp.note ?? undefined,
          permissions: [],
          status: (emp.status as "normal" | "resigned") ?? "normal",
          tags: (emp.employeeTag ?? []).map((t) => ({ id: t.id, name: t.name })),
        })),
      );
    });
  };

  useEffect(() => {
    orpcClient.employee.listRoleQuota({}).then((data) => {
      setRoleQuotas(Array.isArray(data) ? data : []);
    });
    Promise.all([orpcClient.employee.listRole({}), apiUserGetMe()]).then(
      ([roleList, me]) => {
        const list = Array.isArray(roleList) ? roleList : [];
        const currentMember = list.find(
          (u) => u.email === (me as { email: string })?.email,
        );
        if (currentMember) {
          const roles = (currentMember.role ?? "")
            .split(",")
            .map((r: string) => r.trim())
            .filter(Boolean);
          setIsAdminOrOwner(roles.includes("owner") || roles.includes("admin"));
        }
      },
    );
    fetchTags();
    fetchEmployees();
  }, []);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const handleSelectEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEditDialogOpen(true);
  };

  const handleSaveEmployee = async (updatedEmployee: Employee) => {
    try {
      await single(
        orpcClient.employee.update([
          {
            id: updatedEmployee.id,
            name: updatedEmployee.name,
            birthday: updatedEmployee.birthday
              ? new Date(updatedEmployee.birthday)
              : undefined,
            bloodType: updatedEmployee.bloodType,
            nationalIdNo: updatedEmployee.idNumber,
            motorbikeLicense: updatedEmployee.hasMotorcycleLicense,
            carLicense: updatedEmployee.hasCarLicense,
            profile: updatedEmployee.avatar,
            address:
              updatedEmployee.city ||
              updatedEmployee.district ||
              updatedEmployee.postalCode ||
              updatedEmployee.address
                ? {
                    city: updatedEmployee.city ?? "",
                    district: updatedEmployee.district ?? "",
                    postalCode: updatedEmployee.postalCode
                      ? Number(updatedEmployee.postalCode)
                      : 0,
                    address: updatedEmployee.address ?? "",
                  }
                : undefined,
            emergencyContact: updatedEmployee.emergencyContact,
            emergencyContactRelationship: updatedEmployee.emergencyRelation,
            emergencyContactPhoneNumber: updatedEmployee.emergencyPhone,
            note: updatedEmployee.notes,
          },
        ]),
      );
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === updatedEmployee.id ? updatedEmployee : emp,
        ),
      );
    } catch (error) {
      console.error("employee.update_ERROR", error);
    }
  };

  const handleResignEmployee = async (resignedEmployee: Employee) => {
    setEditDialogOpen(false);
    try {
      await orpcClient.employee.resign({ employeeId: resignedEmployee.id });
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === resignedEmployee.id ? { ...emp, status: "resigned" } : emp,
        ),
      );
    } catch (error) {
      console.error("employee.resign_ERROR", error);
    }
  };

  const handleReinstateEmployee = async (reinstatedEmployee: Employee) => {
    setEditDialogOpen(false);
    try {
      await orpcClient.employee.unresign({ employeeId: reinstatedEmployee.id });
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === reinstatedEmployee.id ? { ...emp, status: "normal" } : emp,
        ),
      );
    } catch (error) {
      console.error("employee.unresign_ERROR", error);
    }
  };

  const tabItems = [
    {
      key: "employees",
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          {t("employee.page.employeeListTab")}
        </span>
      ),
      children: (
        <EmployeeList
          employees={employees}
          allTags={allTags}
          onSelectEmployee={handleSelectEmployee}
          onAddEmployee={() => setAddDialogOpen(true)}
        />
      ),
    },
    ...(isAdminOrOwner
      ? [
          {
            key: "permissions",
            label: (
              <span>
                <SafetyOutlined style={{ marginRight: 8 }} />
                {t("employee.page.permissionOverviewTab")}
              </span>
            ),
            children: (
              <Card
                title={t("employee.page.permissionTitle")}
                extra={
                  <span style={{ color: "#999", fontSize: 13 }}>
                    {t("employee.page.permissionHint")}
                  </span>
                }
              >
                <PermissionOverview roleQuotas={roleQuotas} />
              </Card>
            ),
          },
        ]
      : []),
    ...(isAdminOrOwner
      ? [
          {
            key: "licenses",
            label: (
              <span>
                <AppstoreOutlined style={{ marginRight: 8 }} />
                {t("employee.page.licensesTab")}
              </span>
            ),
            children: <LicensesTab />,
          },
        ]
      : []),
    ...(isAdminOrOwner && invoiceEnabled
      ? [
          {
            key: "invoices",
            label: (
              <span>
                <FileTextOutlined style={{ marginRight: 8 }} />
                {t("employee.page.invoiceTab")}
              </span>
            ),
            children: <InvoiceTab />,
          },
        ]
      : []),
  ];

  return (
    <div
      style={{
        padding: 16,
        backgroundColor: "#fff",
        margin: "16px 16px 0",
        borderRadius: "0.75rem",
      }}
    >
      <Tabs defaultActiveKey="employees" items={tabItems} />

      <EmployeeEditModal
        employee={selectedEmployee}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleSaveEmployee}
        onResign={handleResignEmployee}
        onReinstate={handleReinstateEmployee}
        allTags={allTags}
        onTagChange={(employeeId, newTags) => {
          setEmployees((prev) =>
            prev.map((emp) => emp.id === employeeId ? { ...emp, tags: newTags } : emp),
          );
        }}
        onNewTagCreated={() => { fetchTags(); }}
      />

      <AddEmployeeModal
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdded={(newEmployee) => {
          setEmployees((prev) => [...prev, newEmployee]);
        }}
        allTags={allTags}
      />
    </div>
  );
}

export default EmployeesPage;
